/**
 * The page as people open it, drawn for a phone and an iPad: every part of it can be tapped. A tap on words opens that
 * slot's own field; a tap on any other part opens that part's chat. The page itself is the generated page, so its parts
 * and slots are the ones the computer's walk types over.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FOLD, foldDoc } from "../walk/foldUnits";
import { sizeFrame, slotIndex, usePartBefore, WALK_STYLE } from "../walk/pageFrame";
import { type PageStudio, WHOLE_PAGE } from "../walk/pageStudio";

/** The words under a tap: which slot of which part, which one of its kind, and what it says now. */
export interface TappedSlot {
  section: string;
  slot: string;
  index: number;
  text: string;
  /** How many slots of that name the part holds: a "What you will learn" line may go out while others stay. */
  count: number;
}

export function SeasonPageFrame({
  studio,
  tablet,
  selected,
  peek,
  peekPage,
  onSlot,
  onPart,
}: {
  studio: PageStudio;
  /** On an iPad the page stands in a phone, and the part whose chat is open is marked. */
  tablet: boolean;
  selected: string | null;
  /** Hold Before: the open part as it was, or the whole page as it was. */
  peek: boolean;
  peekPage: boolean;
  onSlot: (tapped: TappedSlot) => void;
  onPart: (section: string) => void;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number>(FOLD.phone);
  // Why refs: the page's own listeners are wired once per draw and must reach the latest handlers.
  const slot = useRef(onSlot);
  slot.current = onSlot;
  const part = useRef(onPart);
  part.current = onPart;
  const wholeBefore = peekPage ? studio.before(WHOLE_PAGE) : null;
  const shownDoc = useMemo(() => {
    const doc = wholeBefore !== null ? studio.docOf(wholeBefore) : studio.doc;
    return doc === null ? "" : foldDoc(doc, FOLD.phone);
  }, [wholeBefore, studio]);

  const dress = useCallback(() => {
    const d = frame.current?.contentDocument;
    if (!d?.body) return;
    if (!d.getElementById("bw-style")) {
      const style = d.createElement("style");
      style.id = "bw-style";
      style.textContent = WALK_STYLE;
      d.head.appendChild(style);
    }
    d.querySelectorAll("form").forEach((f) => f.addEventListener("submit", (e) => e.preventDefault()));
    d.querySelectorAll("a[href]").forEach((a) => a.addEventListener("click", (e) => e.preventDefault()));
    const parts = [...d.querySelectorAll<HTMLElement>("[data-section]")].filter((el) => !el.parentElement?.closest("[data-section]"));
    for (const el of parts) {
      const name = el.dataset.section ?? "";
      el.classList.toggle("bw-sel", tablet && name === selected);
      el.classList.toggle("bw-work", name === studio.working);
      el.classList.toggle("bw-landed", name === studio.landed);
      if (el.dataset.bwWired) continue;
      el.dataset.bwWired = "1";
      el.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const words = (e.target as HTMLElement).closest<HTMLElement>("[data-slot]");
        if (words && words.tagName !== "IMG" && el.contains(words)) {
          const same = [...el.querySelectorAll<HTMLElement>("[data-slot]")].filter((s) => s.dataset.slot === words.dataset.slot);
          slot.current({ section: name, slot: words.dataset.slot ?? "", index: slotIndex(el, words), text: (words.textContent ?? "").replace(/\s*\n\s*/g, " ").trim(), count: same.length });
          return;
        }
        part.current(name);
      });
    }
    d.body.classList.toggle("bw-busy", !!studio.working && studio.working !== WHOLE_PAGE);
    if (frame.current) sizeFrame(frame.current, FOLD.phone, setHeight);
  }, [selected, studio.landed, studio.working, tablet]);

  useEffect(() => {
    dress();
  }, [dress]);
  usePartBefore(frame, selected, studio, peek, dress);

  if (studio.doc === null) return <div className="mk-box mk-loadbox" aria-busy="true" aria-label="Opening the page" />;
  const iframe = <iframe ref={frame} className="bw-pframe mk-pframe" title="The page" srcDoc={shownDoc} scrolling="no" onLoad={dress} style={{ height }} />;
  return tablet ? (
    <div className="bw-phwrap">
      <div className="bw-phone bw-auto">
        <div className="bw-phone-in">{iframe}</div>
      </div>
    </div>
  ) : (
    <div className="mk-box">{iframe}</div>
  );
}
